import { formatMoney, toMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Card, H2, Separator, Text } from '@betterment/samba-react';
import { CardLoader } from '@betterment/shared-components';
import { formattedNumberCompact } from '@betterment/shared-components/src/utils/accountSecurityHelpers';

import {
  DirectTradingAccountSecurityStatisticsDocument,
  DirectTradingAccountSecurityStatisticsQuery,
} from './graphql/directTradingAccountSecurityStatistics.generated';

import { FinancialSecuritySecurityType } from '../../../graphql/schema';

type FinancialSecurity = DirectTradingAccountSecurityStatisticsQuery['account']['financialSecurity'];

const UnavailableText: React.FC = () => (
  <Text role="cell" as="p" variant="text50">
    Unavailable
  </Text>
);

type MutualFundProps = {
  financialSecurity: FinancialSecurity;
};

const MutualFundDetails: React.FC<MutualFundProps> = ({ financialSecurity }) => {
  const { statistics } = financialSecurity;

  return (
    <Card.Content css={{ gap: 'space.300' }}>
      <Box css={{ display: 'block', width: '100%' }}>
        <Box
          css={{
            display: 'grid',
            gridTemplateColumns: {
              lg: '1fr 1fr 1fr 1fr',
              base: '1fr 1fr',
            },
            rowGap: 'space.200',
            alignItems: 'start',
          }}
          role="table"
          aria-label="stats"
        >
          {statistics.today && (
            <>
              <Box css={{ gridColumn: { base: '1 / span 2', lg: '1' } }} role="row" aria-label="previous close">
                <Text role="cell" as="p" variant="text25" color="text.weak" css={{ fontWeight: 'normal' }}>
                  Previous close
                </Text>
                {statistics.today.previousClosePrice ? (
                  <Text role="cell" as="p" variant="text50">
                    {formatMoney(toMoney(statistics.today.previousClosePrice.cents), 'default')}
                  </Text>
                ) : (
                  <UnavailableText />
                )}
              </Box>
            </>
          )}

          {statistics.year && (
            <>
              <Box role="row" aria-label="52w low">
                <Text role="cell" as="p" variant="text25" color="text.weak" css={{ fontWeight: 'normal' }}>
                  52W low
                </Text>
                {statistics.year.priceRange?.lowPrice ? (
                  <Text role="cell" as="p" variant="text50">
                    {formatMoney(toMoney(statistics.year.priceRange.lowPrice.cents))}
                  </Text>
                ) : (
                  <UnavailableText />
                )}
              </Box>

              <Box role="row" aria-label="52w high">
                <Text role="cell" as="p" variant="text25" color="text.weak" css={{ fontWeight: 'normal' }}>
                  52W high
                </Text>
                {statistics.year.priceRange?.highPrice ? (
                  <Text role="cell" as="p" variant="text50">
                    {formatMoney(toMoney(statistics.year.priceRange.highPrice.cents))}
                  </Text>
                ) : (
                  <UnavailableText />
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Card.Content>
  );
};

type EtfAndStockProps = {
  financialSecurity: FinancialSecurity;
};

const EtfAndStockDetails: React.FC<EtfAndStockProps> = ({ financialSecurity }) => {
  const { statistics } = financialSecurity;

  return (
    <Card.Content css={{ gap: 'space.300' }}>
      <Box css={{ display: 'block', width: '100%' }}>
        <Box
          css={{
            display: 'grid',
            gridTemplateColumns: {
              lg: '1fr 1fr 1fr 1fr',
              base: '1fr 1fr',
            },
            rowGap: 'space.200',
            alignItems: 'start',
          }}
          role="table"
          aria-label="stats"
        >
          {statistics.today && (
            <>
              <Box role="row" aria-label="open">
                <Text role="cell" as="p" variant="text25" color="text.weak" css={{ fontWeight: 'normal' }}>
                  Open
                </Text>
                {statistics.today.openPrice ? (
                  <Text role="cell" as="p" variant="text50">
                    {formatMoney(toMoney(statistics.today.openPrice.cents))}
                  </Text>
                ) : (
                  <UnavailableText />
                )}
              </Box>

              <Box role="row" aria-label="previous close">
                <Text role="cell" as="p" variant="text25" color="text.weak" css={{ fontWeight: 'normal' }}>
                  Previous close
                </Text>
                {statistics.today.previousClosePrice ? (
                  <Text role="cell" as="p" variant="text50">
                    {formatMoney(toMoney(statistics.today.previousClosePrice.cents), 'default')}
                  </Text>
                ) : (
                  <UnavailableText />
                )}
              </Box>

              <Box
                css={{ gridColumn: { base: 'auto / span 2', lg: 'auto / span 2' } }}
                role="row"
                aria-label="todays volume"
              >
                <Text role="cell" as="p" variant="text25" color="text.weak" css={{ fontWeight: 'normal' }}>
                  Today's volume
                </Text>
                {statistics.today.volume ? (
                  <Text role="cell" as="p" variant="text50">
                    {formattedNumberCompact(statistics.today.volume.cents)}
                  </Text>
                ) : (
                  <UnavailableText />
                )}
              </Box>
            </>
          )}

          {statistics.today && (
            <>
              <Box role="row" aria-label="1d low">
                <Text role="cell" as="p" variant="text25" color="text.weak" css={{ fontWeight: 'normal' }}>
                  1D low
                </Text>
                {statistics.today?.priceRange?.lowPrice ? (
                  <Text role="cell" as="p" variant="text50">
                    {formatMoney(toMoney(statistics.today.priceRange.lowPrice.cents))}
                  </Text>
                ) : (
                  <UnavailableText />
                )}
              </Box>

              <Box role="row" aria-label="1d high">
                <Text role="cell" as="p" variant="text25" color="text.weak" css={{ fontWeight: 'normal' }}>
                  1D high
                </Text>
                {statistics.today?.priceRange?.highPrice ? (
                  <Text role="cell" as="p" variant="text50">
                    {formatMoney(toMoney(statistics.today.priceRange.highPrice.cents))}
                  </Text>
                ) : (
                  <UnavailableText />
                )}
              </Box>
            </>
          )}

          {statistics.year && (
            <>
              <Box role="row" aria-label="52w low">
                <Text role="cell" as="p" variant="text25" color="text.weak" css={{ fontWeight: 'normal' }}>
                  52W low
                </Text>
                {statistics.year.priceRange?.lowPrice ? (
                  <Text role="cell" as="p" variant="text50">
                    {formatMoney(toMoney(statistics.year.priceRange.lowPrice.cents))}
                  </Text>
                ) : (
                  <UnavailableText />
                )}
              </Box>

              <Box role="row" aria-label="52w high">
                <Text role="cell" as="p" variant="text25" color="text.weak" css={{ fontWeight: 'normal' }}>
                  52W high
                </Text>
                {statistics.year.priceRange?.highPrice ? (
                  <Text role="cell" as="p" variant="text50">
                    {formatMoney(toMoney(statistics.year.priceRange.highPrice.cents))}
                  </Text>
                ) : (
                  <UnavailableText />
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Card.Content>
  );
};

type Props = {
  accountId: string;
  securityId: string;
};

export const StatsCard: React.FC<Props> = ({ accountId, securityId }) => {
  const { data } = useGraphQLQuery(DirectTradingAccountSecurityStatisticsDocument, {
    variables: { accountId, securityId },
    throwOnError: true,
  });

  if (!data) return <CardLoader />;

  if (!data.account || data.account.__typename !== 'DirectTradingAccount') {
    return null;
  }

  const { account } = data;
  const { financialSecurity } = account;
  const { securityType } = financialSecurity;

  const isMutualFundType = securityType === FinancialSecuritySecurityType.MutualFund;
  const isEtfType = securityType === FinancialSecuritySecurityType.Etf;
  const isEquityType = securityType === FinancialSecuritySecurityType.Equity;
  const isEtfOrEquityType = isEtfType || isEquityType;

  return (
    <Card css={{ display: 'grid', gap: 'space.300', padding: 'space.300' }}>
      <Card.Header>
        <H2 variant="title200">Details</H2>
      </Card.Header>
      <Separator />
      {isMutualFundType && <MutualFundDetails financialSecurity={financialSecurity} />}
      {isEtfOrEquityType && <EtfAndStockDetails financialSecurity={financialSecurity} />}
    </Card>
  );
};
